import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';

const TEMPLATE_LABELS = {
  request_submitted: 'Request Submitted',
  request_approved: 'Request Approved',
  request_rejected: 'Request Rejected',
  request_cancelled: 'Request Cancelled',
  approver_reminder: 'Approver Reminder',
  approver_assigned: 'Approver Assigned',
};

const AVAILABLE_PLACEHOLDERS = [
  '{{requesterName}}',
  '{{approverName}}',
  '{{leaveType}}',
  '{{startDate}}',
  '{{endDate}}',
  '{{days}}',
  '{{reason}}',
  '{{rejectionReason}}',
  '{{submittedAt}}',
  '{{actionUrl}}',
];

function formatDate(ts) {
  if (!ts) return '\u2014';
  try {
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return ts;
  }
}

export default function TemplatesPage() {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [search, setSearch] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.listTemplates();
      setTemplates(res.templates || []);
    } catch (err) {
      toast.error(`Failed to load templates: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSeedDefaults() {
    if (templates.length > 0 && !window.confirm('Templates already exist. Re-seeding will overwrite them. Continue?')) return;
    setSeeding(true);
    try {
      const res = await adminApi.seed(templates.length > 0);
      toast.success(`Seeded ${res.templates?.created || 0} templates`);
      await load();
    } catch (err) {
      toast.error(`Seed failed: ${err.message}`);
    } finally {
      setSeeding(false);
    }
  }

  const filtered = useMemo(() => {
    if (!search) return templates;
    const s = search.toLowerCase();
    return templates.filter(
      (t) =>
        t.rowKey?.toLowerCase().includes(s) ||
        t.subject?.toLowerCase().includes(s) ||
        TEMPLATE_LABELS[t.rowKey]?.toLowerCase().includes(s)
    );
  }, [templates, search]);

  const columns = [
    {
      key: 'rowKey',
      label: 'Template',
      render: (t) => (
        <div>
          <div className="font-medium">{TEMPLATE_LABELS[t.rowKey] || t.rowKey}</div>
          <div className="text-xs text-muted-foreground">{t.rowKey}</div>
        </div>
      ),
    },
    {
      key: 'subject',
      label: 'Subject',
      render: (t) => (
        <span className="text-sm">
          {t.subject?.length > 50 ? t.subject.substring(0, 50) + '...' : t.subject || '\u2014'}
        </span>
      ),
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (t) => (
        <Badge variant={t.isActive === false ? 'danger' : 'success'}>
          {t.isActive === false ? 'Inactive' : 'Active'}
        </Badge>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      render: (t) => (
        <span className="text-sm text-muted-foreground">{formatDate(t.updatedAt)}</span>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Email Templates</h1>
          <p className="text-muted-foreground">
            {templates.length} templates &middot; Customize the wording of automated emails
          </p>
        </div>
        <Button variant="secondary" onClick={handleSeedDefaults} loading={seeding}>
          Seed defaults
        </Button>
      </div>

      {templates.length > 3 && (
        <div className="mb-5">
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-[400px]"
          />
        </div>
      )}

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage='No templates yet. Click "Seed defaults" to create the standard set.'
        onRowClick={(t) => setEditingTemplate(t)}
        keyFn={(t) => t.rowKey}
      />

      {editingTemplate && (
        <TemplateEditModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSaved={async () => {
            setEditingTemplate(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function TemplateEditModal({ template, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({
    subject: template.subject || '',
    body: template.body || '',
    isActive: template.isActive !== false,
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!form.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    if (!form.body.trim()) {
      toast.error('Body is required');
      return;
    }

    setSaving(true);
    try {
      await adminApi.upsertTemplate(template.rowKey, {
        subject: form.subject,
        body: form.body,
        isActive: form.isActive,
      });
      toast.success(`Updated "${TEMPLATE_LABELS[template.rowKey] || template.rowKey}"`);
      onSaved();
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Edit: ${TEMPLATE_LABELS[template.rowKey] || template.rowKey}`}
      width="640px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>Save changes</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Subject line"
          value={form.subject}
          onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
          placeholder="Email subject..."
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-muted-foreground">
            Email body
          </label>
          <textarea
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            rows={12}
            className="w-full rounded-md border border-border bg-card px-3 py-2.5 font-mono text-sm leading-relaxed text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={{ resize: 'vertical' }}
          />
        </div>

        <div className="rounded-md border border-input bg-muted/50 p-3">
          <div className="mb-1.5 text-xs font-semibold text-muted-foreground">
            Available placeholders
          </div>
          <div className="flex flex-wrap gap-1.5">
            {AVAILABLE_PLACEHOLDERS.map((p) => (
              <code
                key={p}
                className="rounded bg-primary/10 px-2 py-0.5 text-[11px] text-primary"
              >
                {p}
              </code>
            ))}
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border bg-card p-2.5">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
          <div>
            <div className="text-sm font-medium">Active</div>
            <div className="text-xs text-muted-foreground">
              Inactive templates will not be sent.
            </div>
          </div>
        </label>
      </div>
    </Modal>
  );
}
